// src/utils/sat/satBreakdown.js
//
// Pure (no React, no Firestore) computation of the SAT diagnostic subcategory
// breakdown. Lifted verbatim from the inline logic in
// components/admin/dsat/reports/SubcategoryBreakdown.js so the report and the
// SAT plan generator share one implementation.
//
// Inputs:
//   questionIndex: Map<questionId, { subcategory, correctAnswer, ... }>
//   answers:       { [questionId]: studentAnswerString }
//
// Output of computeSatBreakdown():
//   {
//     statsMap,        // normalized subcat key -> { correct, total }
//     perSubMetrics,   // normalized subcat key -> { total, correct, percentage, hours, multiplier }
//     perUnitMetrics,  // "<Subject>||<Unit>"   -> { total, correct, hours, percentage }
//     subjectTotals,   // "<Subject>"           -> summed Rx hours
//   }
//
// These shapes are exactly what the report component consumed before the
// refactor; rendering must be byte-identical.

import {
  SAT_STRUCTURE,
  SUB_MULTIPLIERS,
  normalize,
  pct,
  isCorrect,
} from "./satStructure";

// Build stats[key] from the *answers* for this attempt
export function buildStatsFromAnswers(questionIndex, answers) {
  const stats = {};
  if (!answers) return stats;

  for (const [qid, ansRaw] of Object.entries(answers)) {
    const meta = questionIndex.get(qid);
    if (!meta) continue;

    const subRaw = meta.subcategory || "Uncategorized";
    const key = normalize(subRaw);

    if (!stats[key]) stats[key] = { correct: 0, total: 0 };

    const ok = isCorrect(ansRaw, meta.correctAnswer);
    stats[key].total += 1;
    if (ok) stats[key].correct += 1;
  }

  return stats;
}

/**
 * Compute the full breakdown. This reproduces, step for step, the two useMemo
 * blocks that previously lived in SubcategoryBreakdown.js (statsMap + the
 * per-sub/per-unit/subject-totals accumulation).
 */
export function computeSatBreakdown(questionIndex, answers) {
  const statsMap = buildStatsFromAnswers(questionIndex, answers);

  const perSub = {};
  const perUnit = {};
  const subjTotals = {};

  Object.entries(SAT_STRUCTURE).forEach(([subjectName, units]) => {
    if (!subjTotals[subjectName]) subjTotals[subjectName] = 0;

    Object.entries(units).forEach(([unitName, subcats]) => {
      const unitKey = `${subjectName}||${unitName}`;
      if (!perUnit[unitKey]) {
        perUnit[unitKey] = { total: 0, correct: 0, hours: 0, percentage: null };
      }

      subcats.forEach((subName) => {
        const keyNorm = normalize(subName);
        const data = statsMap[keyNorm] || { total: 0, correct: 0 };
        const total = data.total || 0;
        const correct = data.correct || 0;
        const percentage = pct(correct, total);

        const multiplier = SUB_MULTIPLIERS[keyNorm] || 0;
        let hours = null;

        if (percentage != null && multiplier > 0) {
          const accuracyDecimal = percentage / 100;
          const raw = (1 - accuracyDecimal) * multiplier;
          hours = Math.ceil(raw);
        }

        perSub[keyNorm] = {
          total,
          correct,
          percentage,
          hours,
          multiplier,
        };

        // accumulate into unit / subject
        if (total > 0) {
          perUnit[unitKey].total += total;
          perUnit[unitKey].correct += correct;
        }
        if (hours != null) {
          perUnit[unitKey].hours += hours;
          subjTotals[subjectName] += hours;
        }
      });

      // finalize unit percentage
      const u = perUnit[unitKey];
      u.percentage = pct(u.correct, u.total);
    });
  });

  return {
    statsMap,
    perSubMetrics: perSub,
    perUnitMetrics: perUnit,
    subjectTotals: subjTotals,
  };
}
